import {
  ETIQUETA_CAMPO_EXPORTACION,
  ETIQUETA_CAMPO_EXPORTACION_EN,
  LISTA_DE_CAMPO,
  TIPO_DE_CAMPO,
  type CampoEditable,
  type CatalogosDeSeguimiento,
  type ExportacionResumen,
  type TipoDeCampo,
} from '@cnc/contracts';
import {et, tr, txt, type Texto} from '../i18n/idioma';

// Los campos editables de una exportacion, agrupados como en el formulario,
// y la conversion entre el texto que se escribe y el valor que se envia.
export type Campo = {clave: CampoEditable; tipo: TipoDeCampo; lista?: keyof CatalogosDeSeguimiento; ayuda?: Texto};
export type GrupoDeCampos = {titulo: Texto; nota?: Texto; campos: Campo[]};

const campo = (clave: CampoEditable, ayuda?: Texto): Campo => ({
  clave,
  tipo: TIPO_DE_CAMPO[clave],
  lista: LISTA_DE_CAMPO[clave],
  ayuda,
});

export const GRUPOS_DE_CAMPOS: GrupoDeCampos[] = [
  {
    titulo: txt('Identificación', 'Identification'),
    campos: [
      campo(
        'codigo',
        txt(
          'Al cambiarlo, las bolsas, alertas y documentos siguen ligados.',
          'If you change it, bags, alerts and documents stay linked.',
        ),
      ),
      campo('tipo'),
      campo('estado'),
      campo('exportador'),
    ],
  },
  {
    titulo: txt('Ruta', 'Route'),
    campos: [
      campo('agente'),
      campo('naviera'),
      campo('booking'),
      campo('buque'),
      campo('puertoCarga'),
      campo('puertoDestino'),
      campo('diasLibres'),
      campo('diasEnTransito'),
    ],
  },
  {
    titulo: txt('Fechas', 'Dates'),
    nota: txt(
      'Los cambios de ETD y de las ETA quedan en el historial de fechas. Si cambias una ETA y no la ETA destino, esta se recalcula (Hindustan, si no Sachana, si no Mundra).',
      'ETD and ETA changes are kept in the date history. If you change an ETA but not the destination ETA, the latter is recalculated (Hindustan, otherwise Sachana, otherwise Mundra).',
    ),
    campos: [
      campo('etd'),
      campo('eta'),
      campo('etaMundra'),
      campo('etaSachana'),
      campo('etaHindustan'),
      campo('etaInicial'),
      campo('fechaZarpe'),
      campo('fechaLlegada'),
      campo('fechaTren'),
      campo('puertoActual'),
      campo('etdPuertoActual'),
      campo('fechaLlegadaHindustan'),
      campo('fechaBl'),
      campo('fechaLiquidacion'),
    ],
  },
  {
    titulo: txt('Comercial', 'Commercial'),
    campos: [
      campo('comprador'),
      campo('incoterm'),
      campo('valorAproximadoUsd'),
      campo('facturadora'),
      campo('xau'),
      campo('xag'),
      campo('fechaLbma'),
      campo('financiadaPorBlq'),
    ],
  },
  {
    titulo: txt('Totales reportados', 'Reported totals'),
    nota: txt(
      'Solo cuentan cuando la exportación no tiene Big Bags en el inventario; si los tiene, los totales salen de las bolsas.',
      'They only count when the export has no Big Bags in inventory; if it does, the totals come from the bags.',
    ),
    campos: [
      campo('bigBagsReportados'),
      campo('tonHumedasReportadas'),
      campo('tonSecasReportadas'),
      campo('auGReportados'),
      campo('agGReportados'),
      campo('auOzReportadas'),
      campo('agOzReportadas'),
    ],
  },
  {titulo: txt('Notas', 'Notes'), campos: [campo('notas')]},
];

export const CAMPOS_EDITABLES: Campo[] = GRUPOS_DE_CAMPOS.flatMap(g => g.campos);
export const CAMPO_POR_CLAVE = new Map(CAMPOS_EDITABLES.map(c => [c.clave, c]));

// Valor de un campo como texto para un cuadro de edicion ('' = vacio).
export const aTexto = (valor: unknown) =>
  valor === null || valor === undefined ? '' : typeof valor === 'boolean' ? (valor ? 'si' : 'no') : String(valor);

// Lo que la fila de la tabla tiene en cada campo editable. Los totales
// reportados solo cuentan sin bolsas, y un cero es "sin dato".
export function valorDeFila(x: ExportacionResumen, clave: CampoEditable): unknown {
  const medida = (valor: number) => (x.fuenteDeTotales === 'reportado' && valor !== 0 ? valor : null);
  switch (clave) {
    case 'tonHumedasReportadas':
      return medida(x.tonHumedas);
    case 'tonSecasReportadas':
      return medida(x.tonSecas);
    case 'auGReportados':
      return medida(x.auG);
    case 'agGReportados':
      return medida(x.agG);
    case 'auOzReportadas':
      return medida(x.auOz);
    case 'agOzReportadas':
      return medida(x.agOz);
    default:
      return x[clave];
  }
}

// El valor que la persona ve en la fila, para detectar si otra persona lo
// cambio mientras editaba. Los totales reportados solo se comparan cuando la
// fila los muestra (sin bolsas); si no, no se comprueban.
const TOTALES_REPORTADOS = new Set<CampoEditable>([
  'tonHumedasReportadas',
  'tonSecasReportadas',
  'auGReportados',
  'agGReportados',
  'auOzReportadas',
  'agOzReportadas',
  'bigBagsReportados',
]);
export function valorVistoDeFila(x: ExportacionResumen, clave: CampoEditable): unknown {
  if (TOTALES_REPORTADOS.has(clave)) return undefined;
  return valorDeFila(x, clave);
}

// Convierte el texto escrito al valor que se envia; '' es null (borrar).
export function convertirValor(clave: CampoEditable, texto: string): {valor: unknown} | {error: string} {
  const limpio = texto.trim();
  const tipo = TIPO_DE_CAMPO[clave];
  const etiqueta = et(ETIQUETA_CAMPO_EXPORTACION, ETIQUETA_CAMPO_EXPORTACION_EN)[clave];
  if (limpio === '') {
    if (tipo === 'estado' || tipo === 'tipo' || clave === 'codigo') {
      return {error: tr(`"${etiqueta}" no puede quedar vacío.`, `"${etiqueta}" cannot be empty.`)};
    }
    return {valor: null};
  }
  if (tipo === 'numero' || tipo === 'entero') {
    const n = Number(limpio.replace(',', '.'));
    if (!Number.isFinite(n) || (tipo === 'entero' && !Number.isInteger(n))) {
      return {
        error: tr(
          `El valor de "${etiqueta}" no es un número válido.`,
          `The value of "${etiqueta}" is not a valid number.`,
        ),
      };
    }
    return {valor: n};
  }
  if (tipo === 'booleano') return {valor: limpio === 'si'};
  if (tipo === 'fecha' && !/^\d{4}-\d{2}-\d{2}$/.test(limpio)) {
    return {error: tr(`La fecha de "${etiqueta}" no es válida.`, `The date for "${etiqueta}" is not valid.`)};
  }
  return {valor: limpio};
}

export const esCampoEditable = (id: string): id is CampoEditable => id in TIPO_DE_CAMPO;
